/**
 * paging — a single ``has_more``-following page walker shared by every
 * twin-API "fetch every page" loop (#1973 finding 10). Before this, the
 * identical ``for (page…) { push; if (!has_more) break }`` loop was
 * re-implemented in ``twinService.getAllEntityTransitions`` and in each app
 * hook (``useDeckungsbeitrag`` / ``useLiquiditaetsplanung`` / ``useMatchGroups``).
 *
 * The call sites differ in ONE deliberate way that this helper preserves via
 * ``onCapExceeded``:
 *   - the as-of reconstruction (``getAllEntityTransitions``) must NEVER return a
 *     truncated series as if it were complete — hitting the page cap with the
 *     server still reporting ``has_more`` is an error, so it THROWS (round-3 #3);
 *   - the app hooks bound a runaway loop at a low ``MAX_PAGES`` and are content to
 *     silently TRUNCATE there (the volumes never approach the cap in practice).
 *
 * ``stopOnEmptyBatch`` mirrors ``useMatchGroups``' extra guard: stop as soon as a
 * page returns zero rows even while the server still claims ``has_more`` (defends
 * a pathological server that never flips the flag).
 */

/** One page of a ``has_more``-paginated endpoint. */
export interface PageResult<T> {
  items: T[];
  hasMore: boolean;
}

export interface FetchAllPagesOptions {
  /** Hard upper bound on pages walked (runaway guard). */
  pageCap: number;
  /**
   * What to do when ``pageCap`` is reached while the server STILL reports more:
   * ``'throw'`` refuses to present a truncated series as complete; ``'truncate'``
   * returns what was collected so far.
   */
  onCapExceeded: 'throw' | 'truncate';
  /** Also stop when a page returns an empty batch (even if ``hasMore``). */
  stopOnEmptyBatch?: boolean;
  /** Message for the ``'throw'`` case (a default is used when omitted). */
  capMessage?: string;
}

/** A page walk that also reports whether it stopped short (#2611). */
export interface FetchAllPagesResult<T> {
  items: T[];
  /**
   * True when ``pageCap`` was reached with the server STILL reporting more —
   * i.e. ``items`` is a PARTIAL view. Callers must surface this rather than
   * render the partial list as complete.
   */
  truncated: boolean;
}

/**
 * Walk ``fetchPage`` from page 1 upward, concatenating each page's ``items``,
 * until the server reports no more (or ``stopOnEmptyBatch`` fires). Returns the
 * collected list PLUS whether the walk stopped short.
 *
 * The third way between the two original modes (#2611). ``'throw'`` and
 * ``'truncate'`` are both lossy at opposite extremes: throwing takes down a
 * whole page over a partial feed, truncating hands back a partial feed dressed
 * as a complete one. The Liquiditätsplanung feed used the latter, so once WF's
 * twin outgrew the 10k guard the app rendered a months-stale Bestand with no
 * indication anything was missing — a farmer-reported p1. Returning the flag
 * lets the caller keep rendering AND tell the truth about what it is showing.
 */
export async function fetchAllPagesTracked<T>(
  fetchPage: (page: number) => Promise<PageResult<T>>,
  options: Omit<FetchAllPagesOptions, 'onCapExceeded'>,
): Promise<FetchAllPagesResult<T>> {
  const { pageCap, stopOnEmptyBatch = false } = options;
  const all: T[] = [];
  for (let page = 1; page <= pageCap; page += 1) {
    const { items, hasMore } = await fetchPage(page);
    all.push(...items);
    if (!hasMore) return { items: all, truncated: false };
    if (stopOnEmptyBatch && items.length === 0) return { items: all, truncated: false };
  }
  return { items: all, truncated: true };
}

/**
 * Walk ``fetchPage`` from page 1 upward, concatenating each page's ``items``,
 * until the server reports no more (or ``stopOnEmptyBatch`` fires). Returns the
 * full list. On reaching ``pageCap`` with more still remaining, either throws or
 * returns the partial list per ``onCapExceeded``.
 */
export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<PageResult<T>>,
  options: FetchAllPagesOptions,
): Promise<T[]> {
  const { pageCap, onCapExceeded, stopOnEmptyBatch, capMessage } = options;
  const { items, truncated } = await fetchAllPagesTracked(fetchPage, { pageCap, stopOnEmptyBatch });
  if (truncated && onCapExceeded === 'throw') {
    throw new Error(
      capMessage ?? `fetchAllPages: exceeded ${pageCap} pages with more remaining — refusing to return a truncated series as complete`,
    );
  }
  return items;
}
